export class PropertyDataCleaner {
  /**
   * Deeply cleans an object by removing null, undefined, empty strings, empty arrays, and empty objects.
   */
  static deepClean(obj: unknown): unknown {
    if (obj === null || obj === undefined) return undefined;

    if (Array.isArray(obj)) {
      const cleanedArray = obj
        .map((item) => this.deepClean(item))
        .filter((item) => item !== undefined && item !== null && item !== '');

      return cleanedArray.length > 0 ? cleanedArray : undefined;
    }

    if (typeof obj === 'object' && !(obj instanceof Date)) {
      const cleanedObj: Record<string, unknown> = {};
      let hasValue = false;

      for (const key in obj as Record<string, unknown>) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = (obj as Record<string, unknown>)[key];
          const cleanedValue = this.deepClean(value);

          // For booleans, we keep them as they are meaningful (true/false)
          // For everything else, we only keep if it has a substantial value
          if (
            cleanedValue !== undefined &&
            cleanedValue !== null &&
            cleanedValue !== '' &&
            (typeof cleanedValue !== 'object' ||
              cleanedValue instanceof Date ||
              Object.keys(cleanedValue).length > 0)
          ) {
            cleanedObj[key] = cleanedValue;
            hasValue = true;
          } else if (typeof cleanedValue === 'boolean') {
            cleanedObj[key] = cleanedValue;
            hasValue = true;
          }
        }
      }

      return hasValue ? cleanedObj : undefined;
    }

    return obj === '' ? undefined : obj;
  }

  /**
   * Applies specific business rules for property data cleaning.
   */
  static cleanPropertyData(data: Record<string, unknown>): Record<string, unknown> {
    if (!data) return data;

    // Apply deep clean first
    const cleaned = (this.deepClean(data) || {}) as Record<string, unknown>;

    // Business rule: if not in co-ownership, scrap coOwnership details but keep the flag
    const coOwnership = data['coOwnership'] as { isInCoOwnership?: boolean | null } | undefined;
    if (Object.prototype.hasOwnProperty.call(data, 'coOwnership')) {
      if (coOwnership?.isInCoOwnership === false) {
        cleaned['coOwnership'] = { isInCoOwnership: false };
      } else if (coOwnership?.isInCoOwnership === null || coOwnership?.isInCoOwnership === undefined) {
        delete (cleaned as Record<string, unknown>)['coOwnership'];
      }
    }

    // Business rule: if coOwnership exists and is true, ensure we keep the flag even if everything else was cleaned
    if (
      coOwnership?.isInCoOwnership &&
      !(cleaned['coOwnership'] as { isInCoOwnership?: boolean })?.isInCoOwnership
    ) {
      cleaned['coOwnership'] = {
        ...(cleaned['coOwnership'] as Record<string, unknown>),
        isInCoOwnership: true,
      };
    }

    return cleaned;
  }
}
